import type { AskHelixValidationResult } from './clarification-validation';
import { isPlainDataRecord, readAllowedOwnStringEntries } from './clarification-own-keys';
import { parseClarificationTrustedData } from './clarification-trusted-parse';

type OwnDataValues = ReadonlyMap<string, unknown>;

export type AskHelixResumeAttemptDto = Readonly<{
  pinnedObjectiveDigest: string;
  claimedObjectiveDigest: string;
}>;

/**
 * RUNTIME SEAM: persisted-run load and
 * apps/api/src/ask-helix/manager-runtime/loop/material-input/manager-run-resume.ts:57-60.
 * The catalog-digest half lands with the run store in spec Unit 2.
 */
export function validateObjectiveDigestPin(
  attempt: AskHelixResumeAttemptDto
): AskHelixValidationResult<AskHelixResumeAttemptDto> {
  const trusted = parseClarificationTrustedData(attempt);
  if (!trusted.ok) return objectiveDigestMismatch();
  const fields = readExactDataRecord(
    trusted.value,
    ['pinnedObjectiveDigest', 'claimedObjectiveDigest']
  );
  if (fields === null) return objectiveDigestMismatch();
  const pinnedObjectiveDigest = fields.get('pinnedObjectiveDigest');
  const claimedObjectiveDigest = fields.get('claimedObjectiveDigest');
  if (typeof pinnedObjectiveDigest !== 'string' || typeof claimedObjectiveDigest !== 'string') {
    return objectiveDigestMismatch();
  }
  return pinnedObjectiveDigest === claimedObjectiveDigest
    ? { ok: true, value: { pinnedObjectiveDigest, claimedObjectiveDigest } }
    : { ok: false, reason: 'objective_digest_mismatch' };
}

export type AskHelixResumableRunDto = Readonly<{
  runId: string;
  expiresAt: string;
}>;

/** One nullable, conversation-keyed slot. Concurrent resumable runs are unrepresentable. */
export type AskHelixResumableRunSlotDto = Readonly<{
  conversationId: string;
  slot: AskHelixResumableRunDto | null;
}>;

/**
 * RUNTIME SEAM: supersede-on-new-turn at run bootstrap / run store (spec Unit 2).
 * Atomicity is runtime-only and is deliberately not claimed by this pure contract.
 */
export function validateResumableRunSlot(
  current: AskHelixResumableRunSlotDto,
  requestedRunId: string,
  now: Date
): AskHelixValidationResult<AskHelixResumableRunSlotDto> {
  const trustedCurrent = parseClarificationTrustedData(current);
  if (!trustedCurrent.ok) return runSuperseded();
  const currentFields = readExactDataRecord(trustedCurrent.value, ['conversationId', 'slot']);
  if (currentFields === null) return runSuperseded();
  const conversationId = currentFields.get('conversationId');
  const rawSlot = currentFields.get('slot');
  if (typeof conversationId !== 'string' || rawSlot === null) {
    return { ok: false, reason: 'run_superseded' };
  }
  const slotFields = readExactDataRecord(rawSlot, ['runId', 'expiresAt']);
  if (slotFields === null) return runSuperseded();
  const runId = slotFields.get('runId');
  const expiresAtText = slotFields.get('expiresAt');
  if (typeof runId !== 'string' || typeof expiresAtText !== 'string' || runId !== requestedRunId) {
    return runSuperseded();
  }
  const expiresAt = Date.parse(expiresAtText);
  const trustedNow = parseClarificationTrustedData(now, { allowNativeDate: true });
  const nowMilliseconds =
    trustedNow.ok && typeof trustedNow.value === 'number' ? trustedNow.value : null;
  if (nowMilliseconds === null || !Number.isFinite(expiresAt) || expiresAt <= nowMilliseconds) {
    return { ok: false, reason: 'run_expired' };
  }
  return {
    ok: true,
    value: { conversationId, slot: { runId, expiresAt: expiresAtText } },
  };
}

const SERVER_DERIVED_SCOPE_BRAND: unique symbol = Symbol('AskHelixServerDerivedScope');

export type AskHelixServerDerivedScope = Readonly<{
  tenantId: string;
  actorId: string;
  readonly __brand: typeof SERVER_DERIVED_SCOPE_BRAND;
}>;

export type AskHelixFrozenRunScope = Readonly<{
  tenantId: string;
  actorId: string;
}>;

/**
 * Callable only from a server request context after tenant and actor derivation.
 * Exporting this shared-package factory is not a wire-authority hole: scope is never
 * carried on the wire or parsed from a payload, and AskHelixInputResponseDto has no
 * scope field. A client minting one locally therefore has nothing to send it to. The
 * server derives its own scope at the named RUNTIME SEAM below, where the run-store
 * read remains tenant-scoped.
 */
export function serverDerivedScope(input: AskHelixFrozenRunScope): AskHelixServerDerivedScope;
export function serverDerivedScope(
  input: unknown
): AskHelixServerDerivedScope | AskHelixValidationResult<AskHelixServerDerivedScope>;
export function serverDerivedScope(
  input: unknown
): AskHelixServerDerivedScope | AskHelixValidationResult<AskHelixServerDerivedScope> {
  const trusted = parseClarificationTrustedData(input);
  if (!trusted.ok) return { ok: false, reason: 'identity_scope_mismatch' };
  const scope = readFrozenRunScope(trusted.value);
  if (scope === null) return { ok: false, reason: 'identity_scope_mismatch' };
  return { ...scope, __brand: SERVER_DERIVED_SCOPE_BRAND };
}

/**
 * RUNTIME SEAM: apps/api/src/ask-helix/ask-helix.controller.ts:1317-1326 derives
 * tenant/actor through prepareQueryExecution; the run-store read must itself be tenant-scoped.
 */
export function validateRunScope(
  scope: AskHelixServerDerivedScope,
  frozen: AskHelixFrozenRunScope
): AskHelixValidationResult<AskHelixServerDerivedScope> {
  const parsedScope = parseClarificationTrustedData(scope, { allowSymbols: true });
  const parsedFrozen = parseClarificationTrustedData(frozen);
  if (!parsedScope.ok || !parsedFrozen.ok) {
    return { ok: false, reason: 'identity_scope_mismatch' };
  }
  const trustedScope = readServerDerivedScope(parsedScope.value);
  const frozenScope = readFrozenRunScope(parsedFrozen.value);
  if (trustedScope === null || frozenScope === null) {
    return { ok: false, reason: 'identity_scope_mismatch' };
  }
  return trustedScope.tenantId === frozenScope.tenantId &&
    trustedScope.actorId === frozenScope.actorId
    ? { ok: true, value: trustedScope }
    : { ok: false, reason: 'identity_scope_mismatch' };
}

export function isAskHelixServerDerivedScope(value: unknown): value is AskHelixServerDerivedScope {
  const trusted = parseClarificationTrustedData(value, { allowSymbols: true });
  return trusted.ok && readServerDerivedScope(trusted.value) !== null;
}

function objectiveDigestMismatch(): AskHelixValidationResult<AskHelixResumeAttemptDto> {
  return { ok: false, reason: 'objective_digest_mismatch' };
}

function runSuperseded(): AskHelixValidationResult<AskHelixResumableRunSlotDto> {
  return { ok: false, reason: 'run_superseded' };
}

function readServerDerivedScope(value: unknown): AskHelixServerDerivedScope | null {
  const fields = readExactDataRecord(value, ['tenantId', 'actorId', '__brand']);
  if (fields === null) return null;
  const tenantId = fields.get('tenantId');
  const actorId = fields.get('actorId');
  const brand = fields.get('__brand');
  if (
    typeof tenantId !== 'string' ||
    typeof actorId !== 'string' ||
    brand !== SERVER_DERIVED_SCOPE_BRAND
  ) {
    return null;
  }
  return { tenantId, actorId, __brand: SERVER_DERIVED_SCOPE_BRAND };
}

function readFrozenRunScope(value: unknown): AskHelixFrozenRunScope | null {
  const fields = readExactDataRecord(value, ['tenantId', 'actorId']);
  if (fields === null) return null;
  const tenantId = fields.get('tenantId');
  const actorId = fields.get('actorId');
  return typeof tenantId === 'string' && typeof actorId === 'string' ? { tenantId, actorId } : null;
}

function readExactDataRecord(value: unknown, fields: readonly string[]): OwnDataValues | null {
  if (!isPlainRecord(value)) return null;
  const entries = readAllowedOwnStringEntries(value, (key) => fields.includes(key));
  if (entries === null || entries.length !== fields.length) return null;
  const values: OwnDataValues = new Map(entries);
  return fields.every((field) => values.has(field)) ? values : null;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return isPlainDataRecord(value);
}
